import { memo, useId, useMemo, useState, type ComponentProps } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {ErrorNotice, Loading} from './components';
import type {ErrorDetails} from './lib/types';
import {errorDetails} from './lib/store';
import {useMediaAddress} from './platform/media';
import {platform} from './platform/transport';
import {remarkWorkspaceFiles} from './lib/workspace-file-links';

function ReplyImage({src, alt, title}: {src: string; alt: string; title: string | undefined}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<ErrorDetails | null>(null);
  const media = useMediaAddress(src);
  const failure = media.error ? errorDetails(media.error) : error;
  return <>
    <Loading active={!loaded && failure === null}/>
    {failure ? <ErrorNotice error={failure}/> : media.source ? <img src={media.source} alt={alt} title={title} decoding="async" referrerPolicy="no-referrer"
      onLoad={() => setLoaded(true)} onError={() => {
        setError(errorDetails(new Error(`The reply image could not load: ${src}`)));
      }}/> : null}
  </>;
}

const components: Components = {
  a({ node: _node, href, children, ...props }) {
    if (!href) return <span>{children}</span>;
    const external = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href);
    return <a {...props} href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>{children}</a>;
  },
  img({ src, alt, title }) {
    if (typeof src !== 'string' || !src) return <ErrorNotice error={errorDetails(new Error('The reply image has no supported address.'))}/>;
    return <ReplyImage key={src} src={src} alt={alt ?? ''} title={title}/>;
  },
  p({node, children}) {
    // An image load failure can include block-level error details.
    return node?.children.some(child => child.type === 'element' && child.tagName === 'img')
      ? <div className="chat-image-paragraph">{children}</div> : <p>{children}</p>;
  },
  table({ children }) {
    return <div className="chat-markdown-table" role="region" aria-label="Message table" tabIndex={0}><table>{children}</table></div>;
  }
};

// Polling can replace native history objects without changing saved replies.
// Parse only replies whose text actually changed; image state remains local.
export const ChatMarkdown = memo(function ChatMarkdown({ text, accountId }: { text: string; accountId: string | null }) {
  const messageId = useId();
  const publicOrigin = platform().publicOrigin;
  const plugins = useMemo<NonNullable<ComponentProps<typeof Markdown>['remarkPlugins']>>(() => [remarkGfm,
    [remarkWorkspaceFiles, {accountId, publicOrigin, basePath: import.meta.env.BASE_URL.replace(/\/$/, '')}]], [accountId, publicOrigin]);
  return <div className="chat-markdown">
    <Markdown
      remarkPlugins={plugins}
      remarkRehypeOptions={{ clobberPrefix: `message-${messageId}-` }}
      components={components}
    >{text}</Markdown>
  </div>;
});
